import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ExternalLink, AlertTriangle, CheckCircle, XCircle, Terminal, Activity, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast, Toaster } from "sonner";
import HowItWorks from "@/HowItWorks";
import AdminLogin from "@/AdminLogin";
import AdminDashboard from "@/AdminDashboard";
import { DonateBanner } from "@/components/DonateBanner";
import { DonateButton } from "@/components/DonateButton";
import { SocialShare } from "@/components/SocialShare";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [inputMode, setInputMode] = useState("url");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedRisk, setSelectedRisk] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Pagination state
  const [displayCount, setDisplayCount] = useState(6);
  const ITEMS_PER_PAGE = 6;

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/analyses`);
      setAnalyses(response.data);
    } catch (error) {
      console.error("Error fetching analyses:", error);
      toast.error("Failed to load analyses");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setIsSearching(true);
    setDisplayCount(6); // Reset to initial count when searching
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));
      if (selectedSource) params.append('source', selectedSource);
      if (selectedRisk) params.append('risk', selectedRisk);

      const response = await axios.get(`${API}/analyses/search?${params.toString()}`);
      setAnalyses(response.data);
    } catch (error) {
      console.error("Error searching analyses:", error);
      toast.error("Failed to search analyses");
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSelectedTags([]);
    setSelectedSource("");
    setSelectedRisk("");
    setIsSearching(false);
    setDisplayCount(6); // Reset to initial count
    fetchAnalyses();
  };

  const loadMore = () => {
    setDisplayCount(prevCount => prevCount + ITEMS_PER_PAGE);
  };

  const [streamingStatus, setStreamingStatus] = useState("");
  const [thinkingText, setThinkingText] = useState("");

  const handleAnalyze = async () => {
    if (!url && !text) {
      toast.error("Please provide a URL or paste article text");
      return;
    }

    setAnalyzing(true);
    setStreamingStatus("Initializing...");
    setThinkingText("");

    try {
      const payload = inputMode === "url" ? { url, stream: true } : { text, stream: true };

      // Use fetch for SSE streaming
      const response = await fetch(`${BACKEND_URL}/api/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.substring(6).trim();
            continue;
          }

          if (line.startsWith('data:')) {
            try {
              const data = JSON.parse(line.substring(5).trim());

              // Handle error event
              if (currentEvent === 'error') {
                throw new Error(data.message || 'Analysis failed');
              }

              // Handle status updates
              if (currentEvent === 'status' && data.message) {
                setStreamingStatus(data.message);
              }

              // Handle thinking/progress text
              if ((currentEvent === 'thinking' || currentEvent === 'progress') && data.text) {
                setThinkingText(prev => prev + data.text);
              }

              // Handle completion
              if (currentEvent === 'complete' && data.id) {
                toast.success("Analysis complete!");
                // Mark that user has completed an analysis (for donation banner)
                localStorage.setItem('hasCompletedAnalysis', 'true');
                // Refresh analyses list
                const analysesResponse = await axios.get(`${API}/analyses`);
                setAnalyses(analysesResponse.data);
                // Navigate to analysis page
                navigate(`/analysis/${data.id}`);
                return;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, 'Line:', line);
              if (e.message && e.message !== 'Unexpected end of JSON input') {
                throw e; // Re-throw actual errors
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error analyzing:", error);
      toast.error(error.message || "Failed to analyze article");
    } finally {
      setAnalyzing(false);
      setStreamingStatus("");
      setThinkingText("");
    }
  };

  const getRiskColor = (label) => {
    switch (label) {
      case "Low":
        return "bg-green-500/20 text-green-400 border-green-500/50";
      case "Medium":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      case "High":
        return "bg-red-500/20 text-red-400 border-red-500/50";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getRiskIcon = (label) => {
    switch (label) {
      case "Low":
        return <CheckCircle className="w-4 h-4" />;
      case "Medium":
        return <AlertTriangle className="w-4 h-4" />;
      case "High":
        return <XCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-100" data-testid="home-page">
      {/* Header */}
      <div className="border-b border-cyan-500/20 bg-black/40 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Terminal className="w-8 h-8 text-cyan-400" />
              <div>
                <h1 className="text-2xl font-bold font-mono text-cyan-400 tracking-wider">NARRATIVE SCOUT</h1>
                <p className="text-xs text-gray-500 font-mono">// Psyops Detection System v1.0</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate("/how-it-works")}
                variant="outline"
                className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 font-mono"
              >
                HOW IT WORKS
              </Button>
              <DonateButton variant="outline" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analyze Section */}
        <Card className="mb-8 border-cyan-500/30 bg-black/60 backdrop-blur-sm" data-testid="analyze-section">
          <CardHeader>
            <CardTitle className="text-cyan-400 font-mono text-xl">ANALYZE ARTICLE</CardTitle>
            <CardDescription className="text-gray-400">Paste a URL or article text to detect engineered influence operations</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 bg-gray-900/50">
                <TabsTrigger value="url" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400" data-testid="url-tab">URL</TabsTrigger>
                <TabsTrigger value="text" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400" data-testid="text-tab">Raw Text</TabsTrigger>
              </TabsList>
              <TabsContent value="url">
                <div className="flex gap-2">
                  <Input
                    placeholder="https://example.com/article"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="flex-1 bg-gray-900/50 border-cyan-500/30 focus:border-cyan-400 text-gray-100 font-mono"
                    data-testid="url-input"
                  />
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing || !url}
                    className="bg-cyan-500 hover:bg-cyan-600 text-black font-mono font-bold"
                    data-testid="analyze-url-button"
                  >
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "ANALYZE"}
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="text">
                <div className="space-y-2">
                  <Textarea
                    placeholder="Paste article text here..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="min-h-[150px] bg-gray-900/50 border-cyan-500/30 focus:border-cyan-400 text-gray-100 font-mono text-sm"
                    data-testid="text-input"
                  />
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing || !text}
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-mono font-bold"
                    data-testid="analyze-text-button"
                  >
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "ANALYZE"}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Streaming Feedback */}
        {analyzing && (streamingStatus || thinkingText) && (
          <Card className="border-cyan-500/50 bg-black/60 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-cyan-400 font-mono flex items-center gap-2">
                <Activity className="w-5 h-5 animate-pulse" />
                AI Analysis in Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Message */}
              {streamingStatus && (
                <div className="flex items-center gap-2 text-gray-300">
                  <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                  <span className="font-mono text-sm">{streamingStatus}</span>
                </div>
              )}

              {/* Thinking Text */}
              {thinkingText && !thinkingText.trim().startsWith('{') && (
                <div className="bg-gray-900/50 border border-cyan-500/30 rounded p-4 max-h-48 overflow-y-auto">
                  <div className="text-xs text-cyan-400/70 font-mono mb-2">AI REASONING:</div>
                  <div className="text-sm text-gray-400 font-mono whitespace-pre-wrap">
                    {thinkingText}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Search Section */}
        <Card className="border-cyan-500/30 bg-black/60 backdrop-blur-sm mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-mono text-gray-100 flex items-center gap-2">
              <Search className="w-5 h-5 text-cyan-400" />
              SEARCH ANALYSES
            </CardTitle>
            <CardDescription className="text-gray-500 font-mono text-xs">
              Search by keywords, tags, source, or risk level
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by title, URL, or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 bg-black/40 border-gray-700 text-gray-100 font-mono"
              />
              <Button
                onClick={handleSearch}
                className="bg-cyan-600 hover:bg-cyan-700 text-white font-mono"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              {isSearching && (
                <Button
                  onClick={clearSearch}
                  variant="outline"
                  className="border-gray-700 text-gray-400 hover:text-gray-100 font-mono"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-500 font-mono mb-1 block">SOURCE DOMAIN</label>
                <Input
                  placeholder="e.g., nytimes.com"
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="bg-black/40 border-gray-700 text-gray-100 font-mono text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 font-mono mb-1 block">RISK LEVEL</label>
                <select
                  value={selectedRisk}
                  onChange={(e) => setSelectedRisk(e.target.value)}
                  className="w-full bg-black/40 border border-gray-700 text-gray-100 font-mono text-sm rounded-md px-3 py-2"
                >
                  <option value="">All Levels</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-500 font-mono mb-1 block">TAGS (comma-separated)</label>
                <Input
                  placeholder="e.g., politics, healthcare"
                  value={selectedTags.join(', ')}
                  onChange={(e) => setSelectedTags(e.target.value.split(',').map(t => t.trim()).filter(t => t))}
                  className="bg-black/40 border-gray-700 text-gray-100 font-mono text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Analyses */}
        <div>
          <h2 className="text-2xl font-bold mb-4 text-gray-100 font-mono">
            {isSearching ? 'SEARCH RESULTS' : 'RECENT ANALYSES'}
          </h2>
          {loading ? (
            <div className="flex justify-center py-12" data-testid="loading-indicator">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </div>
          ) : analyses.length === 0 ? (
            <Card className="border-gray-800 bg-black/40" data-testid="no-analyses">
              <CardContent className="py-12 text-center text-gray-500">
                No analyses yet. Start by analyzing an article above.
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="analyses-grid">
                {analyses.slice(0, displayCount).map((analysis) => (
                  <Card
                    key={analysis.id}
                    className="border-cyan-500/30 bg-black/60 backdrop-blur-sm hover:border-cyan-400/50 transition-all cursor-pointer group"
                    onClick={() => navigate(`/analysis/${analysis.id}`)}
                    data-testid={`analysis-card-${analysis.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-base font-mono text-gray-100 group-hover:text-cyan-400 transition-colors line-clamp-2">
                          {analysis.title || "Untitled"}
                        </CardTitle>
                        <Badge className={`${getRiskColor(analysis.risk_label)} flex items-center gap-1 font-mono text-xs whitespace-nowrap`}>
                          {getRiskIcon(analysis.risk_label)}
                          {analysis.risk_label}
                        </Badge>
                      </div>
                      {analysis.source_domain && (
                        <CardDescription className="text-xs text-gray-500 font-mono flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          {analysis.source_domain}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-mono">SCORE</span>
                          <span className="text-2xl font-bold font-mono text-cyan-400">{analysis.total_score}/100</span>
                        </div>
                        {analysis.tags && analysis.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {analysis.tags.slice(0, 3).map((tag, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className="text-xs font-mono border-cyan-500/30 text-cyan-400 bg-cyan-500/10"
                              >
                                {tag}
                              </Badge>
                            ))}
                            {analysis.tags.length > 3 && (
                              <Badge
                                variant="outline"
                                className="text-xs font-mono border-gray-700 text-gray-500"
                              >
                                +{analysis.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                        <div className="text-xs text-gray-600 font-mono">
                          {new Date(analysis.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Load More Button */}
              {displayCount < analyses.length && (
                <div className="flex justify-center mt-6">
                  <Button
                    onClick={loadMore}
                    variant="outline"
                    className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 font-mono"
                  >
                    LOAD MORE ({analyses.length - displayCount} remaining)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AnalysisDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalysis();
  }, [id]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/analyses/${id}`);
      setAnalysis(response.data);
    } catch (error) {
      console.error("Error fetching analysis:", error);
      toast.error("Failed to load analysis");
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (label) => {
    switch (label) {
      case "Low":
        return "bg-green-500/20 text-green-400 border-green-500/50";
      case "Medium":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      case "High":
        return "bg-red-500/20 text-red-400 border-red-500/50";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getScoreColor = (score) => {
    if (score <= 2) return "text-green-400";
    if (score <= 3) return "text-yellow-400";
    return "text-red-400";
  };

  // Helper function to convert [Source: URL] into clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return null;

    // Regex to match [Source: URL] pattern
    const sourcePattern = /\[Source:\s*(https?:\/\/[^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = sourcePattern.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      // Add the link
      const url = match[1];
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1"
        >
          [Source]
          <ExternalLink className="w-3 h-3 inline" />
        </a>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="loading-detail">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Card className="border-red-500/30 bg-black/60">
          <CardContent className="py-12 text-center text-gray-400">
            Analysis not found
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-100" data-testid="analysis-detail-page">
      {/* Header */}
      <div className="border-b border-cyan-500/20 bg-black/40 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Terminal className="w-8 h-8 text-cyan-400" />
              <div>
                <h1 className="text-2xl font-bold font-mono text-cyan-400 tracking-wider">NARRATIVE SCOUT</h1>
                <p className="text-xs text-gray-500 font-mono">// Analysis Report</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate("/how-it-works")}
                variant="outline"
                className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 font-mono"
              >
                HOW IT WORKS
              </Button>
              <DonateButton variant="outline" />
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 font-mono"
                data-testid="back-button"
              >
                BACK
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Article Info */}
        <Card className="mb-8 border-cyan-500/30 bg-black/60 backdrop-blur-sm" data-testid="article-info">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-2xl font-mono text-gray-100 mb-2">{analysis.title || "Untitled Article"}</CardTitle>
                  <SocialShare
                    url={window.location.href}
                    title={`Analysis: ${analysis.title || "Untitled Article"}`}
                  />
                </div>
                {analysis.source_domain && (
                  <CardDescription className="text-sm text-gray-400 font-mono flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    {analysis.source_domain}
                  </CardDescription>
                )}
                {analysis.url && (
                  <a href={analysis.url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:underline font-mono block mt-1">
                    {analysis.url}
                  </a>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center p-6 border border-cyan-500/30 rounded-lg bg-cyan-500/5">
                <div className="text-sm text-gray-400 font-mono mb-2">TOTAL SCORE</div>
                <div className="text-5xl font-bold font-mono text-cyan-400" data-testid="total-score">{analysis.total_score}/100</div>
              </div>
              <div className="text-center p-6 border border-cyan-500/30 rounded-lg bg-cyan-500/5">
                <div className="text-sm text-gray-400 font-mono mb-2">RISK LEVEL</div>
                <Badge className={`${getRiskColor(analysis.risk_label)} text-2xl font-mono px-6 py-2`} data-testid="risk-label">
                  {analysis.risk_label}
                </Badge>
              </div>
              <div className="text-center p-6 border border-cyan-500/30 rounded-lg bg-cyan-500/5">
                <div className="text-sm text-gray-400 font-mono mb-2">ANALYZED</div>
                <div className="text-sm font-mono text-gray-300">{new Date(analysis.timestamp).toLocaleString()}</div>
              </div>
            </div>

            {analysis.tags && analysis.tags.length > 0 && (
              <div className="border-t border-cyan-500/20 pt-4">
                <div className="text-sm text-gray-400 font-mono mb-3">TAGS</div>
                <div className="flex flex-wrap gap-2">
                  {analysis.tags.map((tag, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="font-mono border-cyan-500/30 text-cyan-400 bg-cyan-500/10"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Commentary */}
        {analysis.analysis.commentary && (
          <Card className="mb-8 border-cyan-500/30 bg-black/60 backdrop-blur-sm" data-testid="ai-commentary">
            <CardHeader>
              <CardTitle className="text-xl font-mono text-cyan-400 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                AI ANALYSIS SUMMARY
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{renderTextWithLinks(analysis.analysis.commentary)}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions Analysis */}
        <div>
          <h2 className="text-2xl font-bold mb-4 text-gray-100 font-mono">DETAILED ANALYSIS</h2>
          <div className="space-y-3">
            {analysis.analysis.questions.map((q, idx) => (
              <Card key={idx} className="border-cyan-500/30 bg-black/60 backdrop-blur-sm" data-testid={`question-card-${idx}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500 font-mono">#{idx + 1}</span>
                        <Badge variant="outline" className="text-xs font-mono border-gray-600 text-gray-400">
                          {q.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-base font-normal text-gray-100">{q.question}</CardTitle>
                    </div>
                    <div className="text-right">
                      <div className={`text-4xl font-bold font-mono ${getScoreColor(q.score)}`} data-testid={`question-score-${idx}`}>
                        {q.score}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">/ 5</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-400 leading-relaxed">{renderTextWithLinks(q.justification)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" theme="dark" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/analysis/:id" element={<AnalysisDetail />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
        <DonateBanner />
      </BrowserRouter>
    </div>
  );
}

export default App;